import { Avatar, Button, Popover } from "antd";
import { materialColors } from "utils";
import { IoExitOutline } from "react-icons/io5";
import { useState } from "react";

const ASSIGNMENT_TYPES = [
  // { type: "board_runner", label: "BoardRunner" },
  { type: "resource_block", label: "Resource/Block" },
  { type: "add_on", label: "Add On" },
  { type: "supervising_physician", label: "Supervising Physician" },
  { type: "call", label: "Call" },
  { type: "late_relief_call", label: "Late Relief/Call" },
  { type: "trauma_physician", label: "Trauma Physician" },
];

const BoardRunnerCell = ({
  runner,
  dropRef,
  providers,
  currentResource,
  providerOptions,
  handleUnassignBoardRunner,
  getBoardRunnersApiLoading,
  renderProviderSelectDropdown,
  unassignBoardRunnerApiLoading,
  isNormal = false,
  assignments,
  getBoardAssignmentsApiLoading,
  handleAssignBoardAssignment,
  handleUnassignBoardAssignment,
  assignBoardAssignmentApiLoading,
  unassignBoardAssignmentApiLoading,
  assignmentDrop,
  selectedFacility,
  isBoardRunner,
  isProvider,
}) => {
  const [visiblePopover, setVisiblePopover] = useState(null);

  const handleRemoveProvider = (providerId) => {
    handleUnassignBoardRunner(providerId);
    setVisiblePopover(null);
  };

  /**
   * Remove provider from assignment (TYPE AWARE)
   */
  const handleRemoveAssignmentProvider = (providerId, type) => {
    handleUnassignBoardAssignment({
      providerId,
      type,
    });
    setVisiblePopover(null);
  };

  const renderProviderPopover = (provider, type = null) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 0",
        minWidth: 150,
      }}>
      <Avatar size="small" style={materialColors.get(provider.first_name?.[0])}>
        {provider.first_name?.[0]}
      </Avatar>
      <span style={{ flex: 1, fontSize: 14 }}>{provider.name}</span>
      <Button
        size="small"
        type="text"
        icon={<IoExitOutline />}
        onClick={() =>
          type
            ? handleRemoveAssignmentProvider(provider.id, type)
            : handleRemoveProvider(provider.id)
        }
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 4,
          padding: "2px 6px",
          height: "auto",
        }}
      />
    </div>
  );

  const isAssignmentEnabled = (type) => {
    if (!selectedFacility) return false;
    return Boolean(selectedFacility?.[`is_${type}_active`]);
  };

  // -----------------------------
  // Generate hooks for all assignment types
  // -----------------------------
  const assignmentDropRefs = {};

  ASSIGNMENT_TYPES?.forEach(({ type }) => {
    const assignment = assignments?.find((a) => a.type === type) || {};

    let providerIds = assignment.provider_id;
    if (!providerIds) providerIds = [];
    else if (!Array.isArray(providerIds)) providerIds = [providerIds];

    assignmentDropRefs[type] = isProvider
      ? { assignmentDropRef: null }
      : assignmentDrop({
          type,
          providerIds,
        });
  });

  const renderAssignmentByType = ({ type, label }) => {
    const assignment = assignments?.find((a) => a.type === type) || {};

    // normalize provider IDs
    let providerIds = assignment.provider_id;
    if (!providerIds) providerIds = [];
    else if (!Array.isArray(providerIds)) providerIds = [providerIds];

    const providersList = Array.isArray(assignment?.providers)
      ? assignment.providers
      : [];

    // Use the hook result stored
    const { assignmentDropRef } = assignmentDropRefs[type];

    return (
      <div
        key={type}
        className="assignment-section"
        ref={!isProvider ? assignmentDropRef : null}
        style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong>{label}:</strong>

        {renderProviderSelectDropdown(
          Array.isArray(providers) ? providers : [],
          providerIds,
          type,
          isProvider,
        )}

        {/* Selected providers */}
        {providersList?.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {providersList?.map((provider) =>
              isProvider ? (
                <Avatar
                  key={provider.id}
                  style={{
                    ...materialColors.get(provider.first_name?.[0]),
                    cursor: "pointer",
                  }}>
                  {provider.first_name?.[0]}
                </Avatar>
              ) : (
                <Popover
                  key={provider.id}
                  content={renderProviderPopover(provider, type)}
                  trigger="click"
                  open={visiblePopover === `${type}-${provider.id}`}
                  onOpenChange={(visible) =>
                    setVisiblePopover(visible ? `${type}-${provider.id}` : null)
                  }
                  placement="bottom">
                  <Avatar
                    style={{
                      ...materialColors.get(provider.first_name?.[0]),
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setVisiblePopover(
                        visiblePopover === `${type}-${provider.id}`
                          ? null
                          : `${type}-${provider.id}`,
                      )
                    }>
                    {provider.first_name?.[0]}
                  </Avatar>
                </Popover>
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  const hasAnyAssignmentEnabled = ASSIGNMENT_TYPES.some(({ type }) =>
    Boolean(selectedFacility?.[`is_${type}_active`]),
  );

  const shouldShowBar = Boolean(isBoardRunner || hasAnyAssignmentEnabled);

  if (!shouldShowBar) return null;

  return (
    <div
      className={isNormal ? "boardrunner-row" : ""}
      style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {isBoardRunner && (
        <div ref={!isProvider ? dropRef : null}>
          <strong style={{ marginRight: 8 }}>Board Runner:</strong>
          {getBoardRunnersApiLoading ? (
            <span>Loading...</span>
          ) : runner?.providers && runner?.providers?.length > 0 ? (
            <>
              {renderProviderSelectDropdown(
                providers,
                runner?.provider_id,
                null,
                isProvider,
              )}
              {runner?.providers &&
              runner?.providers?.length &&
              !isProvider > 0 ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {runner?.providers?.map((provider) => (
                    <Popover
                      key={provider.id}
                      content={renderProviderPopover(provider)}
                      trigger="click"
                      open={visiblePopover === provider.id}
                      onOpenChange={(visible) =>
                        setVisiblePopover(visible ? provider.id : null)
                      }
                      placement="bottom">
                      <Avatar
                        style={{
                          ...materialColors.get(provider.first_name?.[0]),
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          setVisiblePopover(
                            visiblePopover === provider.id ? null : provider.id,
                          )
                        }>
                        {provider.first_name?.[0]}
                      </Avatar>
                    </Popover>
                  ))}
                </div>
              ) : (
                <Avatar style={materialColors.get(runner.name?.[0])}>
                  {runner.name?.[0]}
                </Avatar>
              )}
            </>
          ) : isNormal ? (
            renderProviderSelectDropdown(
              providers,
              runner?.provider_id,
              null,
              isProvider,
            )
          ) : (
            renderProviderSelectDropdown(
              "board-runner",
              currentResource,
              providerOptions,
            )
          )}
        </div>
      )}
      {/* Additional assignment types */}
      {selectedFacility &&
        !getBoardAssignmentsApiLoading &&
        ASSIGNMENT_TYPES?.filter(({ type }) => isAssignmentEnabled(type)).map(
          renderAssignmentByType,
        )}
    </div>
  );
};

export default BoardRunnerCell;
